import { effect, inject, Injector } from '@angular/core';
import { SharedStore } from '@shared/store/shared.signal-store';
import { Observable } from 'rxjs';

/**
 * Emits once the current user is in the store, loading them first if nobody has. Guards need this
 * because the account they decide on is only known after that load.
 *
 * Call it in an injection context; the returned observable can be subscribed to later.
 */
export const whenCurrentUserLoaded = (): Observable<void> => {
  const sharedStore = inject(SharedStore);
  // A guard runs in its route's environment injector, which lives as long as the session does.
  const injector = inject(Injector);

  return new Observable<void>(subscriber => {
    if (!sharedStore.currentUserLoaded()) {
      sharedStore.loadCurrentUser();
    }

    // Unlike toObservable(), the watcher is tied to the subscription rather than to the injector,
    // so a guard that runs on every navigation does not leave one behind each time.
    const watcher = effect(
      () => {
        if (sharedStore.currentUserLoaded()) {
          subscriber.next();
          subscriber.complete();
        }
      },
      { injector, manualCleanup: true },
    );

    return () => watcher.destroy();
  });
};
